import '@uiw/react-md-editor/markdown-editor.css';

import { useCallback, useState, useEffect } from 'react';
import MDEditor from '@uiw/react-md-editor';
import { Modal } from '@trinity/chat-components/components/Modal';
import { cx } from '@trinity/chat-components/utils';
import styles from './style/MarkdownEditor.module.css';

type Props = {
  /**
   * Whether the modal is open
   */
  open: boolean;

  /**
   * The initial markdown value to display in the editor
   */
  value?: string;

  /**
   * Callback fired when the user clicks Save.
   * Receives the current markdown string.
   * Return a promise to show loading state on the Save button.
   */
  onSave: (value: string) => Promise<void> | void;

  /**
   * Callback fired when the modal is closed (Cancel or overlay click)
   */
  onClose: () => void;

  /**
   * Whether the Save button should be disabled (beyond the default logic)
   */
  disabled?: boolean;

  /**
   * Whether a save operation is in progress (shows loading state)
   */
  isSaving?: boolean;

  /**
   * Custom label for the Save button
   * @default "Save"
   */
  saveLabel?: string;

  /**
   * Custom label for the Cancel button
   * @default "Cancel"
   */
  cancelLabel?: string;

  /**
   * Optional title to display at the top of the modal
   */
  title?: string;

  /**
   * Accessibility label for the modal
   */
  'aria-label'?: string;

  /**
   * Optional custom class names
   */
  classes?: {
    root?: string;
    modal?: string;
    editor?: string;
    buttons?: string;
  };
};

/**
 * A reusable Markdown Editor component with a modal interface.
 * This is a controlled component that wraps @uiw/react-md-editor.
 * 
 * The component provides:
 * - Full markdown editing with preview
 * - Save/Cancel buttons with loading states
 * - Automatic dirty-state tracking (Save disabled when unchanged)
 * 
 * @example
 * ```tsx
 * <MarkdownEditor
 *   open={isOpen}
 *   value={persona}
 *   onSave={async (value) => {
 *     await updatePersona(value);
 *     setIsOpen(false);
 *   }}
 *   onClose={() => setIsOpen(false)}
 *   isSaving={mutation.isPending}
 *   title="Edit Persona"
 * />
 * ```
 */
export const MarkdownEditor = ({
  open,
  value = '',
  onSave,
  onClose,
  disabled = false,
  isSaving = false,
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  title,
  'aria-label': ariaLabel = 'Markdown editor dialog',
  classes = {},
}: Props) => {
  const [markdownValue, setMarkdownValue] = useState<string | undefined>(value);

  // Reset the internal value when the modal opens with a new value
  useEffect(() => {
    if (open) {
      setMarkdownValue(value);
    }
  }, [open, value]);

  const handleSave = useCallback(() => {
    onSave(markdownValue ?? '');
  }, [markdownValue, onSave]);

  const handleClose = useCallback(() => {
    onClose();
  }, [onClose]);

  // Disable save if: explicitly disabled, no content, or content unchanged
  const isSaveDisabled = disabled || !markdownValue || markdownValue === value;

  return (
    <Modal
      open={open}
      onClose={handleClose}
      classes={{
        root: cx(styles.modalRoot, classes.modal),
      }}
      aria-label={ariaLabel}>
      <div className={cx(styles.root, classes.root)}>
        {title && <h2 className={styles.title}>{title}</h2>}
        <div className={cx(styles.editorContainer, classes.editor)}>
          <MDEditor
            value={markdownValue}
            onChange={setMarkdownValue}
            height="100%"
            data-color-mode="light" />
        </div>
        <div className={cx(styles.buttons, classes.buttons)}>
          <button
            type="button"
            className={cx(styles.button, styles.buttonPrimary)}
            disabled={isSaveDisabled || isSaving}
            onClick={handleSave}>
            {isSaving ? (
              <span className={styles.loadingSpinner} />
            ) : null}
            {saveLabel}
          </button>
          <button
            type="button"
            className={cx(styles.button, styles.buttonSecondary)}
            onClick={handleClose}>
            {cancelLabel}
          </button>
        </div>
      </div>
    </Modal>
  );
};

MarkdownEditor.displayName = 'MarkdownEditor';
